// src/receptionist/nameTracker.ts
//
// Which business name a merchant-config screen should save (#4937). The
// sidebar's pencil renames without touching a screen's state (a new prop would
// reset its unsaved draft), so each screen tracks two names: the one it was
// handed (`shown`) and the newest one announced for the merchant (`latest`).
// A draft still carrying `shown` is saved with `latest`.
//
// `revision` counts announcements. A read or save that STARTED before an
// announcement answers with an older name than the one announced since; it may
// fill the screen, but it never replaces `latest` and is never announced, or
// the sidebar would go back to the old name and the next save would write it
// (Jack, #4938). No React here, so the rules can be checked on their own.

export type NameTracker = { shown: string | null; latest: string | null; revision: number };

export function newNameTracker(): NameTracker {
    return { shown: null, latest: null, revision: 0 };
}

/** A name announced for this merchant: by the pencil, or by any read or save. */
export function nameAnnounced(t: NameTracker, name: string): void {
    t.revision += 1;
    t.latest = name;
}

/**
 * The server's answer to a read or save that began at `revisionAtStart`. The
 * screen is handed `name` either way. True when it is still the newest and may
 * be announced; false when a newer name was announced while it was out.
 */
export function nameReceived(t: NameTracker, name: string, revisionAtStart: number): boolean {
    t.shown = name;
    if (t.revision !== revisionAtStart) return false;
    t.latest = name;
    return true;
}

/** The name to PUT for a draft: the newest, when the draft still has the name it was handed. */
export function nameToSave(t: NameTracker, draftName: string): string {
    const renamedElsewhere = t.latest !== null && t.latest !== t.shown && draftName === t.shown;
    return renamedElsewhere ? (t.latest as string) : draftName;
}
